import {
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  IconButton
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { OrganizationNotificationTrigger } from '../../../services/Hub/notificationCenter/types';
import DispatchSetupsEditor from './DispatchSetupsEditor';
import { ASTQueryBuilder } from '../../../shared/components/ASTQueryBuilder/ASTQueryBuilder';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEngineSourceFields } from '../../../redux/notificationCenter';
import { RootState } from '../../../redux/store';
import { mongoQueryToRuleGroup } from '@/shared/components/ASTQueryBuilder/utils/mongoQueryToRuleGroup';
import { conditionEngineToRuleGroup } from '@/shared/components/ASTQueryBuilder/utils/ConditionEngineToRuleGroup';
import { engineSourceFieldsToFieldDefinition } from '@/shared/components/ASTQueryBuilder/utils/EngineSourceFieldsToFieldDefinition';
import { Notifications } from '../../../services/Hub/notificationCenter/notificationCenterApi';
import { FullScreenModal } from '@/shared/components/FullScreenModal';

interface EditTriggerModalProps {
  open: boolean;
  onClose: () => void;
  trigger: OrganizationNotificationTrigger | null;
  onSave: (triggerData: Partial<OrganizationNotificationTrigger>) => void;
  isLoading?: boolean;
}

export default function EditTriggerModal({
  open,
  onClose,
  trigger,
  onSave,
  isLoading = false
}: EditTriggerModalProps) {
  const dispatch = useDispatch();
  const { engineSourceFields, isLoading: isLoadingFields } = useSelector(
    (state: RootState) => state.notificationCenter
  );

  const [editFormData, setEditFormData] = useState<Partial<OrganizationNotificationTrigger>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [dispatchSetupData, setDispatchSetupData] = useState<any>(null);
  const [isJsonExpanded, setIsJsonExpanded] = useState(false);

  const crmMembershipFields = useMemo(() => {
    if (!engineSourceFields) return [];

    return engineSourceFields.filter(
      field => field.components?.dynamicOptions?.resource === 'CRM:Memberships'
    );
  }, [engineSourceFields]);

  // Initialize form when modal opens
  useEffect(() => {
    if (trigger && open) {
      setEditFormData({
        name: trigger.name,
        description: trigger.description,
        source: trigger.source,
        isActive: trigger.isActive,
        conditions: trigger.conditions,
        mongoQuery: trigger.mongoQuery
      });

      // Initialize dispatch setup data
      if (trigger.notificationDispatchSetups?.[0]) {
        setDispatchSetupData({
          id: trigger.notificationDispatchSetups[0].id,
          notificationMessageTemplateId:
            trigger.notificationDispatchSetups[0].notificationMessageTemplateId,
          recipientUserIds: trigger.notificationDispatchSetups[0].recipientUserIds,
          additionalPayloadDefinitionConfig:
            trigger.notificationDispatchSetups[0].additionalPayloadDefinitionConfig
        });
      }

      // Fetch fields for the trigger source
      if (trigger.source) {
        (dispatch as any)(
          fetchEngineSourceFields({
            triggerSource: trigger.source,
            organizationId: trigger.ownerOrganizationId || undefined
          })
        );
      }
    }
  }, [trigger, open]); // Removido dispatch das dependências

  const handleFormChange = useCallback(
    (field: keyof OrganizationNotificationTrigger, value: any) => {
      setEditFormData(prev => ({ ...prev, [field]: value }));
      // Clear validation errors when user makes changes
      setValidationErrors([]);
    },
    []
  );

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!editFormData.name?.trim()) {
      errors.push('Nome é obrigatório');
    }

    if (!editFormData.source?.trim()) {
      errors.push('Fonte é obrigatória');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (trigger) {
      setIsSaving(true);
      try {
        // Save trigger data
        await onSave({
          ...editFormData,
          id: trigger.id
        });

        // Save dispatch setup if it exists and has changes
        if (dispatchSetupData && trigger.notificationDispatchSetups?.[0]) {
          await Notifications.dispatchSetups.update(dispatchSetupData.id, {
            recipientUserIds: dispatchSetupData.recipientUserIds,
            additionalPayloadDefinitionConfig: dispatchSetupData.additionalPayloadDefinitionConfig
          });
          console.log('Dispatch setup updated successfully');
        }
      } catch (error) {
        console.error('Error saving trigger:', error);
        setValidationErrors(['Falha ao salvar trigger. Tente novamente.']);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleClose = useCallback(() => {
    setEditFormData({});
    setDispatchSetupData(null);
    setIsSaving(false);
    setValidationErrors([]);
    onClose();
  }, [onClose]);

  const handleDispatchSetupChange = useCallback((setup: any) => {
    setDispatchSetupData(setup);
  }, []);

  const handleASTQueryChange = useCallback(
    (mongoQuery: Record<string, any>) => {
      handleFormChange('mongoQuery', mongoQuery);
    },
    [handleFormChange]
  );

  // Memoização de cálculos expensivos
  const mappedFields = useMemo(
    () => engineSourceFieldsToFieldDefinition(engineSourceFields),
    [engineSourceFields]
  );

  const initialQuery = useMemo(() => {
    if (editFormData.mongoQuery) {
      return mongoQueryToRuleGroup(editFormData.mongoQuery);
    }
    if (editFormData.conditions) {
      return conditionEngineToRuleGroup(editFormData.conditions);
    }
    return undefined;
  }, [editFormData.mongoQuery, editFormData.conditions]);

  // Memoização dos componentes filhos
  const memoizedASTQueryBuilder = useMemo(
    () => (
      <ASTQueryBuilder
        initialQuery={initialQuery}
        onChange={handleASTQueryChange}
        fields={mappedFields}
        organizationId={trigger?.ownerOrganizationId || undefined}
      />
    ),
    [initialQuery, handleASTQueryChange, mappedFields, trigger?.ownerOrganizationId]
  );

  const memoizedDispatchSetupsEditor = useMemo(
    () => (
      <DispatchSetupsEditor
        dispatchSetups={trigger?.notificationDispatchSetups?.[0]}
        organizationId={trigger?.ownerOrganizationId || undefined}
        engineSourceFields={crmMembershipFields}
        onDispatchSetupChange={handleDispatchSetupChange}
        dispatchSetupData={dispatchSetupData}
      />
    ),
    [
      trigger?.notificationDispatchSetups,
      trigger?.ownerOrganizationId,
      crmMembershipFields,
      handleDispatchSetupChange,
      dispatchSetupData
    ]
  );

  const getStatusColor = useCallback(() => {
    return trigger?.isActive ? 'success' : 'default';
  }, [trigger?.isActive]);

  const getStatusIcon = useCallback(() => {
    return trigger?.isActive ? <CheckCircleIcon /> : <WarningIcon />;
  }, [trigger?.isActive]);

  if (!trigger) return null;

  return (
    <FullScreenModal
      open={open}
      onClose={handleClose}
      title={`Editar Trigger de Notificação - ${trigger.name}`}
      subtitle={`ID: ${trigger.id}`}
      primaryAction={{
        label: isSaving ? 'Salvando...' : 'Salvar Alterações',
        onClick: handleSave,
        disabled: isSaving,
        loading: isSaving
      }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, md: 3 },
              px: { xs: 2, md: 4 }
            }}>
            <Box
              sx={{
                maxWidth: '1200px',
                width: '100%',
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 3, md: 4 }
              }}>
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Erros de Validação</AlertTitle>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Section 1: Basic Information and Metadata */}
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }
                }}>
                {/* Basic Information */}
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e9ecef'
                  }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                    📝 Informações Básicas
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                      label="Nome do Trigger"
                      value={editFormData.name || ''}
                      onChange={e => handleFormChange('name', e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      placeholder="Ex: Notificar quando tarefa for criada"
                      sx={{ width: '100%' }}
                    />

                    <TextField
                      label="Descrição"
                      value={editFormData.description || ''}
                      onChange={e => handleFormChange('description', e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      size="small"
                      placeholder="Descreva quando este trigger deve ser ativado..."
                      sx={{ width: '100%' }}
                    />

                    <Box sx={{ alignSelf: 'flex-start' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={editFormData.isActive ? <CheckCircleIcon /> : <WarningIcon />}
                          label={editFormData.isActive ? 'Ativo' : 'Inativo'}
                          color={editFormData.isActive ? 'success' : 'default'}
                          size="medium"
                          onClick={() => handleFormChange('isActive', !editFormData.isActive)}
                          sx={{ cursor: 'pointer' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          (Clique para alternar)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Metadata */}
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    backgroundColor: '#f0f7ff',
                    borderRadius: 2,
                    border: '1px solid #d1ecf1'
                  }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                    ℹ️ Metadados
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Fonte
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          icon={<CodeIcon />}
                          label={editFormData.source || 'Não definida'}
                          color={editFormData.source ? 'primary' : 'default'}
                          variant={editFormData.source ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Criado Por
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {trigger.createdByUserId}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Criado Em
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {new Date(trigger.createdAt).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Atualizado Em
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {new Date(trigger.updatedAt).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        ID da Organização
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {trigger.ownerOrganizationId || 'Não atribuído'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Section 3: AST Query Builder */}
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px solid #e9ecef'
                }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                  🔧 Condições do Trigger
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Configure as condições que ativam este trigger
                </Typography>

                {isLoadingFields ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : mappedFields.length > 0 ? (
                  memoizedASTQueryBuilder
                ) : (
                  <Alert severity="warning">
                    <AlertTitle>Nenhum Campo Disponível</AlertTitle>
                    Nenhum campo está disponível para esta fonte. Você pode inserir JSON manualmente
                    abaixo.
                  </Alert>
                )}

                {/* JSON Output Section */}
                {editFormData.mongoQuery && Object.keys(editFormData.mongoQuery).length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            color: 'primary.dark'
                          }
                        }}
                        onClick={() => setIsJsonExpanded(!isJsonExpanded)}>
                        Visualizar JSON
                        {isJsonExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Typography>
                    </Box>
                    <Collapse in={isJsonExpanded}>
                      <Box
                        component="pre"
                        sx={{
                          fontSize: '0.875rem',
                          backgroundColor: '#f8f9fa',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          fontFamily: 'monospace',
                          border: '1px solid #e9ecef'
                        }}>
                        {JSON.stringify(editFormData.mongoQuery, null, 2)}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>

              {/* Section 4: Dispatch Setups */}
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor: '#f8f5ff',
                  borderRadius: 2,
                  border: '1px solid #e1d5f7'
                }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2.5, color: 'primary.main' }}>
                  📤 Configuração de Envio
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  Configure como as notificações devem ser enviadas
                </Typography>

                {trigger.notificationDispatchSetups &&
                trigger.notificationDispatchSetups.length > 0 ? (
                  memoizedDispatchSetupsEditor
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <NotificationsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Nenhuma Configuração de Envio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma configuração de envio está configurada para este trigger.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </FullScreenModal>
  );
}
